import Child from './Child';
import { constructMap } from '../libs';

function addItem(key, value) {
  if (key === undefined) {
    throw new Error('Usage: Key is required.');
  }

  let item;

  if (value === undefined) {
    item = this.constructItem(key);
  } else {
    item = value;
  }

  item.parent(this);

  return item;
}

class List extends Child {
  constructor(args = {}) {
    const {
      data = [],
    } = args;

    super(args);

    // Store the list of nouns
    this.itemList = data;

    // Support for multiple filters
    this.filterList = [];

    // Support for a single order by
    this.orderByFields = [];

    // Ensure unique values
    constructMap(this.itemList);
  }

  * [Symbol.iterator]() {
      yield* this.itemList[Symbol.iterator]();
  }

  empty(args) {
    return this.list(args).length === 0;
  }

  length(args) {
    return this.list(args).length;
  }

  removeItem(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    this.itemList = [
      ...this.itemList.filter(v => v.id() !== key), // Remove this value if it already existed
    ];

    return this;
  }

  removeItemByIndex(index) {
    if (index === undefined) {
      throw new Error('Usage: Index is required.');
    }

    this.itemList = this.itemList.filter((_, i) => index !== i);

    return this;
  }

  removeAllItems() {
    this.itemList = [];

    return this;
  }

  filterItems(fn) {
    if (fn === undefined) {
      throw new Error('Usage: fn is required.');
    }

    this.itemList = this.itemList.filter(fn);

    return this;
  }

  findItem(fn) {
    if (fn === undefined) {
      throw new Error('Usage: fn is required.');
    }

    return this.itemList.find(fn);
  }

  constructItem(key) {
    throw new Error('Must be overridden.');
  }

  pushItems(values) {
    values.forEach((value, i) => {
      if (value.id()) {
        this.pushItem(value.id(), value);
      } else {
        this.pushItem(i, value);
      }
    });

    return this;
  }

  /** Attempts to add an item to the end of the list.
   *  NOTE: This will overwrite an existing item if the key already exists in the list.
   */
  pushItem(key, value) {
    const item = addItem.call(this, key, value);

    this.itemList = [
      ...this.itemList.filter(v => v.id() !== key), // Remove this value if it already exists
      item,
    ];

    return item;
  }

  /**
   * Adds an item to the List given a valid index.
   * @param key
   * @param value
   * @param index
   * @returns object
   */
  addItemAtIndex(key, value, index) {
    const item = addItem.call(this, key, value);
    const filteredItems = this.itemList.filter(v => v.id() !== key);
    if (typeof index !== 'number') {
      throw new Error('Index must be a number and defined in List.addItemAtIndex')
    }

    if (index < 0 || index >= filteredItems.length) {
      throw new Error('Index out of range in List.addItemAtIndex');
    }

    filteredItems.splice(index, 0, item)
    this.itemList = [...filteredItems];
    return item
  }

  /**
   * Replace an item in the List given a valid index.
   * @param index - index to replace
   * @param value
   * @returns List
   */
  replaceAtIndex(index, value) {
    if (typeof index !== 'number') {
      throw new Error('Index must be a number and defined in List.addItemAtIndex')
    }

    if (index < 0 || index >= this.itemList.length) {
      throw new Error('Index out of range in List.addItemAtIndex');
    }

    const nextItems = [...this.itemList];
    nextItems[index] = value

    this.itemList = nextItems;

    return this
  }

  index(idx) {
    if (idx === undefined || idx === null) {
      throw new Error('Index is required in List.index');
    }
    if (idx < 0 || idx >= this.itemList.length) {
      throw new Error('Index out of range in List.index');
    }
    return this.itemList[idx];
  }

  unshiftItem(key, value) {
    const item = addItem.call(this, key, value);

    this.itemList = [
      item,
      ...this.itemList.filter(v => v.id() !== key), // Remove this value if it already existed
    ];

    return item;
  }

  updateItem(updatedItem, tempId = null) {
    const updatedItemId = tempId || updatedItem.id();
    const itemIndex = this.itemList.findIndex(v => v.id() === updatedItemId);

    this.itemList = [
      ...this.itemList.slice(0, itemIndex),
      updatedItem,
      ...this.itemList.slice(itemIndex + 1)
    ];

    return this
  }

  _mergeWithReplace(otherList) {
    if (!this.shouldMerge(otherList)) {
      return;
    }

    this.removeAllItems();

    const otherListItems = otherList.list({ ignoreFilters: true })
      .filter(v => v.id() !== undefined);

    for (let i = 0; i < otherListItems.length; i++) {
      this.pushItem(otherListItems[i].id(), otherListItems[i]);
    }

    return this;
  }

  merge(otherList) {
    if (!this.shouldMerge(otherList)) {
      return;
    }

    // Behaviour: If the override includes an override then it completely replaces the override in the original meta.
    if (otherList.orderByFields.length > 0) {
      this.orderByFields = [...otherList.orderByFields];
    }

    const otherFilterNames = otherList.filterList.map(v => v.name);

    this.filterList = [
      ...this.filterList.filter(v => !otherFilterNames.includes(v.name)),
      ...otherList.filterList,
    ];

    const ids = this.list({ ignoreFilters: true }).map(v => v.id());
    const newItems = otherList.list({ ignoreFilters: true })
      .filter(v => v.id() !== undefined)
      .filter(v => !ids.includes(v.id()));

    for (let i = 0; i < newItems.length; i++) {
      this.pushItem(newItems[i].id(), newItems[i]);
    }

    return this;
  }


  /**
   * Set the order by for this list.
   */
  orderBy(fieldNames) {
    if (Array.isArray(fieldNames)) {
      if (fieldNames.length > 1) {
        throw new Error('Ordering by multiple fields is not supported.');
      }

      this.orderByFields = fieldNames;
    } else {
      this.orderByFields = [fieldNames];
    }

    return this;
  }

  addFilter(name, func) {
    if (!(name && func)) {
      throw new Error('Name and func both required.');
    }

    const matches = this.filterList.filter(v => v.name === name);

    if (matches.length > 1) {
      throw new Error('Only 1 function of each name is allowed.');
    } else if (matches.length === 1) {
      matches[0].func = func;
    } else {
      this.filterList = [
        ...this.filterList,
        { name, func },
      ];
    }

    return this;
  }

  /**
   * Remove a filter by name
   */
  removeFilter(name) {
    this.filterList = [
      ...this.filterList.filter(v => v.name !== name),
    ];

    return this;
  }

  /**
   * List the items in this list and apply filters/sort
   *
   * ignoreFilters: if true then filters are skipped.
   */
  list(args = {}) {
    let items = [...this.itemList]; // Never modify the item list directly.

    if (!args.ignoreFilters) {
      for (let i = 0; i < this.filterList.length; i++) {
        items = items.filter(this.filterList[i].func);
      }
    }

    // This length is guaranteed to be 0 or 1.
    if (this.orderByFields.length > 0) {
      const orderByField = this.orderByFields[0];

      const orderByUndefinedList = items.filter(v => v.attr(orderByField) === undefined);
      const orderByDefinedList = items.filter(v => v.attr(orderByField) !== undefined);

      items = [
        ...orderByDefinedList.sort((a, b) => {
          const aValue = a.attr(orderByField);
          const bValue = b.attr(orderByField);

          if (aValue < bValue) return -1;
          if (aValue > bValue) return 1;
          return 0;
        }),
        ...orderByUndefinedList,
      ];
    }

    return items;
  }

  // Return the first element in the list
  head(args) {
    return this.list(args)[0];
  }

  // Returns all but the first element in a list
  tail(args) {
    const list = this.list(args);

    return list.slice(1, list.length);
  }

  // Returns the last element in a list
  last(args) {
    const list = this.list(args);

    return list[list.length - 1];
  }
}

export default List;
